import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { FieldType, FieldTypeConfig, FormlyModule } from "@ngx-formly/core";
import { LocaleService } from "../../services/locale.service";
import { LanguageOption } from "../../types/locale.types";

/**
 * Multi-select language picker with a Material autocomplete, for filters that
 * accept an array of ISO language codes (e.g. the candidate `LanguageCodes`
 * filter). Searches the full, UI-localized language list
 * (LocaleService.availableLanguages), restricts input to valid languages, and
 * stores the selection as `string[]` of ISO codes.
 *
 * Markup mirrors the other search filters (multi-select-panel, location-autocomplete):
 * `mb-3` wrapper, `h5` label, and a bordered input group for visual consistency.
 *
 * Usage:
 * ```
 * { key: 'LanguageCodes', type: 'language-select', props: { label: 'Candidate language' } }
 * ```
 */
@Component({
  selector: "app-formly-language-select",
  standalone: true,
  imports: [TranslateModule, MatAutocompleteModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="mb-3">
      @if (props.label) {
      <h5
        class="font-alternate text-sm font-semibold text-black tracking-tight mb-1.5"
      >
        {{ props.label | translate }}
      </h5>
      }

      <div
        class="flex rounded border border-gray-200 overflow-hidden focus-within:ring-1 focus-within:ring-mty-primary focus-within:border-mty-primary transition-shadow"
      >
        <span
          class="inline-flex items-center px-2.5 text-gray-400 bg-gray-50 border-r border-gray-200"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
        </span>
        <input
          #input
          type="text"
          class="flex-1 min-w-0 px-3 py-1.5 text-sm border-0 outline-none bg-white"
          [matAutocomplete]="auto"
          (input)="query.set($any($event.target).value)"
          [placeholder]="
            $any(props['placeholder'] || 'Type to search a language')
              | translate
          "
        />
        <mat-autocomplete
          #auto="matAutocomplete"
          [displayWith]="displayEmpty"
          (optionSelected)="onSelect($event)"
        >
          @for (opt of filtered(); track opt.code) {
          <mat-option [value]="opt">{{ opt.name }}</mat-option>
          }
        </mat-autocomplete>
      </div>

      @if (selectedOptions().length) {
      <div class="flex flex-wrap gap-1.5 mt-2">
        @for (lang of selectedOptions(); track lang.code) {
        <button
          type="button"
          class="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-mty-primary text-white cursor-pointer select-none transition-all duration-100 active:scale-95"
          (click)="remove(lang.code)"
          [attr.aria-label]="('Remove' | translate) + ' ' + lang.name"
        >
          {{ lang.name
          }}<span class="opacity-70 ml-0.5 text-sm leading-none">×</span>
        </button>
        }
      </div>
      }
    </div>
  `,
})
export class LanguageSelectFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild("input") private input?: ElementRef<HTMLInputElement>;

  /** All selectable languages, localized to the current UI language. */
  private readonly all = computed(() => this.locale.availableLanguages());

  /** Currently selected ISO codes (mirrors the form control). */
  readonly codes = signal<string[]>([]);

  /** Current search term. */
  readonly query = signal<string>("");

  /** Selected codes resolved to their language options for chip display. */
  readonly selectedOptions = computed<LanguageOption[]>(() => {
    const byCode = new Map(this.all().map((l) => [l.code, l]));
    return this.codes().map(
      (code) =>
        byCode.get(code) ?? { code, name: code, nativeName: code, flag: "" }
    );
  });

  /** Autocomplete suggestions: top matches for the query, excluding selected. */
  readonly filtered = computed<LanguageOption[]>(() => {
    const t = this.query().trim().toLowerCase();
    const chosen = new Set(this.codes());
    const pool = this.all().filter((l) => !chosen.has(l.code));
    const matches = t
      ? pool.filter(
          (l) => (l.name + " " + l.nativeName).toLowerCase().indexOf(t) > -1
        )
      : pool;
    return matches.slice(0, 10);
  });

  ngOnInit(): void {
    this.codes.set(this.readControlCodes());

    // Stay in sync when the model is reset/cleared externally (e.g. "clear filters").
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const next = this.readControlCodes();
        if (next.join(",") !== this.codes().join(",")) {
          this.codes.set(next);
        }
      });
  }

  // The input is a search box, not a value display — keep it empty after selection.
  displayEmpty = (): string => "";

  onSelect(event: MatAutocompleteSelectedEvent): void {
    const opt = event.option.value as LanguageOption;
    if (opt?.code && !this.codes().includes(opt.code)) {
      this.commit([...this.codes(), opt.code]);
    }
    this.query.set("");
    this.clearInput();
  }

  remove(code: string): void {
    this.commit(this.codes().filter((c) => c !== code));
  }

  private commit(codes: string[]): void {
    this.codes.set(codes);
    this.formControl.setValue(codes);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  private readControlCodes(): string[] {
    const value = this.formControl.value;
    return Array.isArray(value) ? [...value] : [];
  }

  private clearInput(): void {
    if (this.input) this.input.nativeElement.value = "";
  }
}
